import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Table, FormControl } from 'react-bootstrap';
import AdvancedSelect from '../../../common/AdvancedSelect';
import { makeRESTApiRequest } from '../../../../helpers/api';
import { ENDPOINTS, MAX_DECIMAL_PLACES } from '../../../../helpers/constants';
import { toast } from 'sonner';
import AddEditCityContactModal from '../../../CityContacts/AddEditCityContactModal';
import { ShieldCheck } from 'react-bootstrap-icons';
import { components } from 'react-select';
import CurrencyInput from '../../../common/CurrencyInput';
import useMunicipalityCounty from '../../../../hooks/useMunicipalityCounty';
import './styles.scss';

const ACCOUNT_TYPES = {
  INDIVIDUAL: 'Individual',
  COLLECTED_THROUGH_TAXES: 'Collected through taxes',
  MASTER_METER: 'Master Meter',
  PRIVATE_HAULER: 'Private Hauler',
  WELL_AND_SEPTIC: 'Well & Septic'
};

const ACCOUNT_TYPE_OPTIONS = [
  ACCOUNT_TYPES.INDIVIDUAL,
  ACCOUNT_TYPES.COLLECTED_THROUGH_TAXES,
  ACCOUNT_TYPES.MASTER_METER,
  ACCOUNT_TYPES.PRIVATE_HAULER,
  ACCOUNT_TYPES.WELL_AND_SEPTIC
];
const SERVICE_TYPES = {
  WATER: 'Water',
  SEWER: 'Sewer',
  STORM_WATER: 'Storm Water',
  SOLID_WASTE: 'Solid Waste',
  OTHER: 'Other'
};

const SERVICE_TYPE_OPTIONS = [
  SERVICE_TYPES.WATER,
  SERVICE_TYPES.SEWER,
  SERVICE_TYPES.STORM_WATER,
  SERVICE_TYPES.SOLID_WASTE,
  SERVICE_TYPES.OTHER
];
const TABLE_CONTROL_HEIGHT = '40px';

const formatProviderOption = pvr => {
  return {
    label: pvr?.providerName || ' - ',
    value: pvr?._id,
    info: {
      contactNumber: pvr?.contactNumbers?.length > 0 ? pvr.contactNumbers[0] : '',
      contactEmail: pvr?.contactEmails?.length > 0 ? pvr.contactEmails[0] : '',
      mailPaymentTo: pvr.mailPaymentTo || '',
      providerName: pvr?.providerName,
      department: pvr?.department,
      city: pvr?.city,
      state: pvr?.state
    }
  };
};

const normalizeServiceType = serviceType => {
  if (!serviceType) return '';
  if (serviceType === 'Utility Other Services') return 'Other';
  return serviceType;
};

const defaultRow = id => ({
  id,
  accountType: '',
  provider: '',
  serviceTypes: [],
  accountNumber: '',
  amountDue: '',
  contactNumber: '',
  contactEmail: '',
  mailPaymentTo: ''
});

const isRowPopulated = row =>
  Boolean(
    row.accountType ||
      row.provider ||
      row.serviceTypes.length ||
      row.amountDue ||
      row.accountNumber ||
      row.contactNumber ||
      row.contactEmail ||
      row.mailPaymentTo
  );

const customOptionComponent = ({ data, options, ...props }) => {
  const info = useMemo(() => {
    return data.info || {};
  }, [data]);

  return (
    <components.Option {...props}>
      {info ? (
        <div className="d-flex align-items-center hover-light">
          <ShieldCheck size={14} />
          <div className="flex-grow-1 ml-2 mr-1 py-0">
            {!info.__isNew__ ? (
              <>
                <h6 className="fw-bold midFont mb-0">{info?.providerName || data?.label}</h6>
                <h6 className="tinyFont mb-0">
                  {(info.city || info.state) && (
                    <small>
                      {info.city && info.city + ', '}
                      {info.state}
                    </small>
                  )}
                </h6>
              </>
            ) : (
              <h6 className="fw-bold midFont mb-0">{data.label}</h6>
            )}
          </div>
        </div>
      ) : (
        ''
      )}
    </components.Option>
  );
};

const customSingleValueComponent = ({ data, options, ...props }) => {
  const selectedProvider = useMemo(() => {
    return data;
  }, [data]);

  return (
    <components.SingleValue {...props}>
      {selectedProvider ? (
        <div className="d-flex align-items-center">
          <ShieldCheck size={12} />
          <div className="flex-grow-1 mx-1">
            <h6 className="fw-bold tinyFont my-0">{selectedProvider.info?.providerName}</h6>
            <h6 className="xtinyFont my-0">
              {(selectedProvider.info?.city || selectedProvider.info?.state) && (
                <small>
                  {selectedProvider.info?.city && selectedProvider.info?.city + ', '}
                  {selectedProvider.info?.state}
                </small>
              )}
            </h6>
          </div>
        </div>
      ) : (
        ''
      )}
    </components.SingleValue>
  );
};

const ProviderDropdown = ({
  row = {},
  onProviderSelect = () => {},
  disabled = false,
  selectedServices = [],
  unavailableProviderIds = [],
  isInvalid,
  county
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [addProviderMeta, setAddProviderMeta] = useState({
    show: false,
    initialValues: null
  });
  const [originalOptions, setOriginalOptions] = useState([]);
  const { refreshRecords } = useMunicipalityCounty();
  const currentProviderId = row.provider?._id || row.provider || '';
  const unavailableProviderSet = useMemo(() => new Set(unavailableProviderIds.filter(Boolean)), [
    unavailableProviderIds
  ]);

  const filterUnavailableProviders = useCallback(
    options =>
      options.filter(option => {
        const optionId = option?.value;
        if (!optionId || optionId === currentProviderId) return true;
        return !unavailableProviderSet.has(optionId);
      }),
    [currentProviderId, unavailableProviderSet]
  );

  // One provider request at a time: { key, controller, promise }. A call for the same
  // query + filters reuses the in-flight request (the dropdown's initial load and the
  // mount effect below both ask for the full list); a different one aborts it.
  const abortControllerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const pendingSearchResolveRef = useRef(null);

  const buildProviderFilters = () => {
    // Keep county filter behavior for Storm Water / Solid Waste providers.
    if (
      county &&
      selectedServices.some(type => type === SERVICE_TYPES.STORM_WATER || type === SERVICE_TYPES.SOLID_WASTE)
    ) {
      // lowercase 'string' = case-insensitive match in the backend's constructFilterQuery
      return [{ key: 'state', value: county, type: 'string' }];
    }
    return [];
  };

  const fetchProviderData = (inputVal = '') => {
    if (disabled) return Promise.resolve([]);

    const filters = buildProviderFilters();
    const key = JSON.stringify({ query: inputVal.trim(), filters });
    if (abortControllerRef.current?.key === key) return abortControllerRef.current.promise;

    abortControllerRef.current?.controller.abort();
    const controller = new AbortController();
    const promise = requestProviders(inputVal, filters, controller);
    abortControllerRef.current = { key, controller, promise };
    return promise;
  };

  const requestProviders = async (inputVal, filters, controller) => {
    setIsLoading(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.PROVIDER_CONTACTS_SEARCH,
        method: 'POST',
        requestBody: {
          query: inputVal,
          filters: filters,
          pageNumber: 1,
          pageSize: 200
        },
        signal: controller.signal
      });

      // Covers ABORT_ERROR and a response that resolved just before abort() was called.
      if (controller.signal.aborted) return [];

      if (error) {
        toast.error(error || 'Failed to load providers');
        return [];
      }

      if (response && response?.results) {
        const formattedOptions = filterUnavailableProviders(response.results.map(formatProviderOption));
        setItems(formattedOptions);
        if (!inputVal) {
          setOriginalOptions(formattedOptions);
        }
        return formattedOptions;
      }
      return [];
    } catch (err) {
      console.error('Error fetching provider options:', err);
      return [];
    } finally {
      if (abortControllerRef.current?.controller === controller) {
        abortControllerRef.current = null;
        setIsLoading(false);
      }
    }
  };

  const cancelPendingSearch = () => {
    clearTimeout(searchTimerRef.current);
    pendingSearchResolveRef.current?.([]);
    pendingSearchResolveRef.current = null;
  };

  // react-select renders whatever this promise resolves to, so it must resolve
  // with the results for this exact input (debounced), never the previous list.
  const fetchProviderOptions = (inputVal = '') => {
    cancelPendingSearch();

    if (!inputVal.trim()) {
      return fetchProviderData('');
    }

    return new Promise(resolve => {
      pendingSearchResolveRef.current = resolve;
      searchTimerRef.current = setTimeout(async () => {
        pendingSearchResolveRef.current = null;
        resolve(await fetchProviderData(inputVal));
      }, 300);
    });
  };

  useEffect(
    () => () => {
      cancelPendingSearch();
      abortControllerRef.current?.controller.abort();
    },
    []
  );

  const provider = row.provider;
  const accountTypeValue = row.accountType;

  useEffect(() => {
    if (accountTypeValue === ACCOUNT_TYPES.INDIVIDUAL && items.length === 0) {
      fetchProviderData();
    }
  }, [accountTypeValue, county, selectedServices.join('|'), unavailableProviderIds.join('|')]);

  useEffect(() => {
    setItems(prevItems => filterUnavailableProviders(prevItems));
    setOriginalOptions(prevOptions => filterUnavailableProviders(prevOptions));
  }, [filterUnavailableProviders]);

  useEffect(() => {
    if (provider && accountTypeValue === ACCOUNT_TYPES.INDIVIDUAL) {
      const providerOption = items?.find(item => item?.value === (provider?._id || provider));
      if (!providerOption) {
        setSelectedOption(formatProviderOption(providerOption || provider));
      } else {
        setSelectedOption(providerOption);
      }
    } else {
      setSelectedOption(null);
    }
  }, [accountTypeValue, provider, items]);

  const handleCreateOption = inputValue => {
    setAddProviderMeta({
      show: true,
      initialValues: {
        providerName: inputValue || '',
        makeCheckPayableTo: inputValue || '',
        mailPaymentTo: '',
        howToPay: [],
        payOnlineUrl: '',
        city: '',
        state: '',
        department: selectedServices.length ? selectedServices : ['Water'],
        contactNumbers: [],
        contactEmails: []
      }
    });
  };

  const handleCreateOptionSubmit = async form => {
    setIsLoading(true);
    try {
      const requestData = {
        providerName: form.providerName,
        mailPaymentTo: form.mailPaymentTo,
        howToPay: form.howToPay,
        makeCheckPayableTo: form.makeCheckPayableTo,
        payOnlineUrl: form.payOnlineUrl,
        state: form.state,
        city: form.city,
        department: form.department,
        contactNumbers: form.contactNumbers,
        contactEmails: form.contactEmails
      };

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.CREATE_PROVIDER_CONTACTS,
        method: 'POST',
        requestBody: requestData
      });

      if (error) {
        toast.error(error);
        return;
      }

      if (response) {
        toast.success('Provider created successfully!');
        const newProvider = formatProviderOption(response);

        setSelectedOption(newProvider);
        handleChange(newProvider);
        setItems(prevItems => [...prevItems, newProvider]);
        setOriginalOptions(prevOptions => [...prevOptions, newProvider]);
        setAddProviderMeta({ show: false, initialValues: null });
        refreshRecords();
      }
    } catch (err) {
      console.error('Error creating provider option:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = selectedValue => {
    if (selectedValue) {
      onProviderSelect({
        provider: selectedValue?.value,
        contactNumber: selectedValue?.info?.contactNumber || '',
        contactEmail: selectedValue?.info?.contactEmail || '',
        mailPaymentTo: selectedValue?.info?.mailPaymentTo || '',
        departments: Array.isArray(selectedValue?.info?.department) ? selectedValue.info.department : []
      });
    } else {
      onProviderSelect({
        provider: '',
        contactNumber: '',
        contactEmail: '',
        mailPaymentTo: '',
        departments: []
      });
    }
    setSelectedOption(selectedValue);
  };

  return (
    <div>
      <AdvancedSelect
        items={items}
        originalOptions={originalOptions}
        selectedItems={selectedOption}
        onChange={handleChange}
        isMulti={false}
        disabled={disabled}
        placeholder={disabled ? '-' : isLoading ? '' : 'Select or search'}
        closeMenuOnSelect={true}
        creatable={true}
        searchable={true}
        height={TABLE_CONTROL_HEIGHT}
        onCreateOption={handleCreateOption}
        isLoading={isLoading}
        loadOptions={fetchProviderOptions}
        CustomOptionComponent={customOptionComponent}
        CustomSingleValueComponent={customSingleValueComponent}
        isInvalid={isInvalid}
      />
      <AddEditCityContactModal
        style={{ zIndex: '999999' }}
        show={addProviderMeta.show}
        onHide={() => !isLoading && setAddProviderMeta({ show: false, initialValues: null })}
        mode="add"
        activeTab="Provider"
        entityName="Provider Contact"
        initialValues={addProviderMeta.initialValues || {}}
        onSubmit={handleCreateOptionSubmit}
        loading={isLoading}
      />
    </div>
  );
};

const UtilityServicesTableInput = ({
  formValues = {},
  onChange = () => {},
  showValidationErrors,
  setValidationStatus,
  county
}) => {
  const [rows, setRows] = useState([defaultRow(0)]);
  const [nextRowId, setNextRowId] = useState(1);

  const hasProvider = accountType => accountType === ACCOUNT_TYPES.INDIVIDUAL;
  const canEditAccountNumber = accountType => accountType === ACCOUNT_TYPES.INDIVIDUAL;
  const canEditAmountDue = accountType => accountType === ACCOUNT_TYPES.INDIVIDUAL;
  const canEditContact = accountType => accountType === ACCOUNT_TYPES.INDIVIDUAL;

  useEffect(() => {
    const { services } = formValues || {};
    if (Array.isArray(services)) {
      const normalizedRows = services.map((service, index) => ({
        id: index,
        accountType: service.accountType || '',
        provider: service.provider || '',
        serviceTypes: Array.isArray(service.serviceTypes)
          ? service.serviceTypes.map(normalizeServiceType).filter(Boolean)
          : [],
        accountNumber: service.accountNumber || '',
        amountDue: service.amountDue ?? '',
        contactNumber: service.contactNumber || '',
        contactEmail: service.contactEmail || '',
        mailPaymentTo: service.mailPaymentTo || ''
      }));
      setRows(normalizedRows.length ? normalizedRows : [defaultRow(0)]);
      setNextRowId(normalizedRows.length ? normalizedRows.length : 1);
    } else {
      setRows([defaultRow(0)]);
      setNextRowId(1);
    }
  }, [formValues]);

  const getClaimedServiceTypes = excludeRowId => {
    const claimed = new Set();
    rows.forEach(row => {
      if (row.id === excludeRowId) return;
      row.serviceTypes.forEach(type => claimed.add(type));
    });
    return claimed;
  };

  const getClaimedProviderIds = (excludeRowId, accountType) => {
    if (!accountType) return [];
    const claimed = new Set();
    rows.forEach(row => {
      if (row.id === excludeRowId) return;
      if (row.accountType !== accountType) return;
      const providerId = row.provider?._id || row.provider;
      if (providerId) claimed.add(providerId);
    });
    return Array.from(claimed);
  };

  const isRowValidForCrossRowRules = row => {
    if (hasProvider(row.accountType) && !row.provider) {
      return false;
    }
    return true;
  };

  const toPayload = rowsValue =>
    rowsValue
      .filter(row => row.accountType || row.provider || row.serviceTypes.length || row.amountDue || row.accountNumber)
      .map(row => {
        const payload = {
          serviceTypes: row.serviceTypes,
          accountType: row.accountType || undefined,
          provider: row.provider || undefined,
          accountNumber: row.accountNumber || undefined,
          amountDue:
            row.amountDue === '' || row.amountDue === null || row.amountDue === undefined
              ? undefined
              : Number(row.amountDue),
          contactNumber: row.contactNumber || undefined,
          contactEmail: row.contactEmail || undefined,
          mailPaymentTo: row.mailPaymentTo || undefined
        };

        Object.keys(payload).forEach(key => {
          if (payload[key] === undefined || (Array.isArray(payload[key]) && payload[key].length === 0)) {
            delete payload[key];
          }
        });
        return payload;
      });

  const emitRows = nextRows => {
    onChange(toPayload(nextRows));
    if (setValidationStatus) setValidationStatus(false);
  };

  const updateRow = (rowId, updater) => {
    const nextRows = rows.map(row => (row.id === rowId ? updater(row) : row));
    setRows(nextRows);
    emitRows(nextRows);
  };

  const addRow = () => {
    const hasInvalidRow = rows.some(row => !isRowValidForCrossRowRules(row));
    if (hasInvalidRow) {
      toast.warning('Please complete the required field');
      return;
    }
    const newRow = defaultRow(nextRowId);
    const nextRows = [...rows, newRow];
    setRows(nextRows);
    setNextRowId(prev => prev + 1);
    emitRows(nextRows);
  };

  const removeRow = rowId => {
    if (rows.length === 1) {
      const emptyRow = defaultRow(nextRowId);
      setRows([emptyRow]);
      setNextRowId(prev => prev + 1);
      emitRows([emptyRow]);
      return;
    }
    const nextRows = rows.filter(row => row.id !== rowId);
    setRows(nextRows.length ? nextRows : [defaultRow(nextRowId)]);
    if (!nextRows.length) setNextRowId(prev => prev + 1);
    emitRows(nextRows.length ? nextRows : [defaultRow(nextRowId)]);
  };

  const totalAmount = rows.reduce((sum, row) => {
    const parsed = parseFloat(String(row.amountDue || '').replace(/[^0-9.]/g, ''));
    return sum + (isNaN(parsed) ? 0 : parsed);
  }, 0);

  const accountTypeOptions = ACCOUNT_TYPE_OPTIONS.map(option => ({ label: option, value: option }));

  return (
    <div className="mt-2">
      <div className="utility-services-table-wrap">
        <Table borderless className="utility-services-table mb-0">
          <colgroup>
            <col style={{ width: '180px' }} />
            <col style={{ width: '260px' }} />
            <col style={{ width: '260px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '180px' }} />
            <col style={{ width: '220px' }} />
            <col style={{ width: '260px' }} />
            <col style={{ width: '40px' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="text-center">Account Type</th>
              <th className="text-center">Provider</th>
              <th className="text-center">Services</th>
              <th className="text-center">Account #</th>
              <th className="text-center">Amount Due</th>
              <th className="text-center">Contact Number</th>
              <th className="text-center">Contact Email</th>
              <th className="text-center">Mail Payment To</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const rowHasProvider = hasProvider(row.accountType);
              const rowCanEditAccountNumber = canEditAccountNumber(row.accountType);
              const rowCanEditAmountDue = canEditAmountDue(row.accountType);
              const rowCanEditContact = canEditContact(row.accountType);
              const isProviderTypeWithProvider = hasProvider(row.accountType) && Boolean(row.provider);
              const claimed = getClaimedServiceTypes(row.id);
              const availableServiceTypes = SERVICE_TYPE_OPTIONS.filter(
                serviceType => !claimed.has(serviceType) || row.serviceTypes.includes(serviceType)
              );

              return (
                <tr key={row.id} style={{ backgroundColor: index % 2 === 1 ? '#fafbfc' : '#fff' }}>
                  <td className="align-middle">
                    <AdvancedSelect
                      items={accountTypeOptions}
                      originalOptions={accountTypeOptions}
                      selectedItems={accountTypeOptions.find(option => option.value === row.accountType)}
                      onChange={selectedValue =>
                        updateRow(row.id, current => {
                          const nextType = selectedValue?.value || '';
                          if (!hasProvider(nextType)) {
                            return {
                              ...current,
                              accountType: nextType,
                              provider: '',
                              serviceTypes: [],
                              accountNumber: '',
                              amountDue: '',
                              contactNumber: '',
                              contactEmail: '',
                              mailPaymentTo: ''
                            };
                          }
                          return { ...current, accountType: nextType };
                        })
                      }
                      isMulti={false}
                      placeholder="Select..."
                      closeMenuOnSelect={true}
                      creatable={false}
                      searchable={false}
                      height={TABLE_CONTROL_HEIGHT}
                    />
                  </td>
                  <td className="align-middle">
                    {rowHasProvider ? (
                      <ProviderDropdown
                        row={row}
                        disabled={!row.accountType}
                        selectedServices={row.serviceTypes}
                        unavailableProviderIds={getClaimedProviderIds(row.id, row.accountType)}
                        county={county}
                        isInvalid={showValidationErrors && rowHasProvider && !row.provider}
                        onProviderSelect={providerData => {
                          updateRow(row.id, current => {
                            const claimedTypes = getClaimedServiceTypes(current.id);
                            const suggestedTypes =
                              providerData.departments
                                ?.map(normalizeServiceType)
                                ?.filter(type => SERVICE_TYPE_OPTIONS.includes(type) && !claimedTypes.has(type)) || [];

                            return {
                              ...current,
                              provider: providerData.provider,
                              contactNumber: providerData.contactNumber,
                              contactEmail: providerData.contactEmail,
                              mailPaymentTo: providerData.mailPaymentTo,
                              serviceTypes: current.serviceTypes.length ? current.serviceTypes : suggestedTypes
                            };
                          });
                        }}
                      />
                    ) : (
                      <p className="text-muted text-center mb-0">
                        {row?.accountType === ACCOUNT_TYPES.MASTER_METER
                          ? 'building meter'
                          : row?.accountType === ACCOUNT_TYPES.PRIVATE_HAULER
                          ? 'private hauler'
                          : row?.accountType === ACCOUNT_TYPES.COLLECTED_THROUGH_TAXES
                          ? 'via collected through taxes'
                          : '-'}
                      </p>
                    )}
                  </td>
                  <td className="align-middle">
                    {!row.accountType ? (
                      <p className="text-muted text-center mb-0">-</p>
                    ) : rowHasProvider && !row.provider ? (
                      <span className="text-muted small">Select provider first</span>
                    ) : (
                      <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                        {availableServiceTypes.map(serviceType => {
                          const active = row.serviceTypes.includes(serviceType);
                          return (
                            <button
                              key={serviceType}
                              type="button"
                              onClick={() =>
                                updateRow(row.id, current => {
                                  const exists = current.serviceTypes.includes(serviceType);
                                  const nextServiceTypes = exists
                                    ? current.serviceTypes.filter(type => type !== serviceType)
                                    : [...current.serviceTypes, serviceType];
                                  return { ...current, serviceTypes: nextServiceTypes };
                                })
                              }
                              className={`utility-service-pill ${active ? 'utility-service-pill--active' : ''}`}
                            >
                              <span className="utility-service-pill__dot" />
                              {serviceType}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="align-middle">
                    {rowCanEditAccountNumber && isProviderTypeWithProvider ? (
                      <FormControl
                        type="text"
                        value={row.accountNumber}
                        placeholder="#"
                        style={{ height: TABLE_CONTROL_HEIGHT }}
                        onChange={e => updateRow(row.id, current => ({ ...current, accountNumber: e.target.value }))}
                      />
                    ) : (
                      <p className="text-muted text-center mb-0">-</p>
                    )}
                  </td>
                  <td className="align-middle">
                    {rowCanEditAmountDue && (isProviderTypeWithProvider || row.serviceTypes.length) ? (
                      <CurrencyInput
                        allowExpressions
                        placeholder=""
                        value={row.amountDue}
                        onChange={value => updateRow(row.id, current => ({ ...current, amountDue: value }))}
                        decimalPlaces={MAX_DECIMAL_PLACES}
                        height={TABLE_CONTROL_HEIGHT}
                      />
                    ) : (
                      <p className="text-muted text-center mb-0">-</p>
                    )}
                  </td>
                  <td className="align-middle">
                    {rowCanEditContact && (isProviderTypeWithProvider || row.serviceTypes.length) ? (
                      <FormControl
                        type="text"
                        style={{ height: TABLE_CONTROL_HEIGHT }}
                        value={row.contactNumber}
                        placeholder="Phone number"
                        onChange={e => updateRow(row.id, current => ({ ...current, contactNumber: e.target.value }))}
                      />
                    ) : (
                      <p className="text-muted text-center mb-0">-</p>
                    )}
                  </td>
                  <td className="align-middle">
                    {rowCanEditContact && (isProviderTypeWithProvider || row.serviceTypes.length) ? (
                      <FormControl
                        type="text"
                        value={row.contactEmail}
                        placeholder="Email"
                        style={{ height: TABLE_CONTROL_HEIGHT }}
                        onChange={e => updateRow(row.id, current => ({ ...current, contactEmail: e.target.value }))}
                      />
                    ) : (
                      <p className="text-muted text-center mb-0">-</p>
                    )}
                  </td>
                  <td className="align-middle">
                    {rowCanEditContact && (isProviderTypeWithProvider || row.serviceTypes.length) ? (
                      <FormControl
                        type="text"
                        value={row.mailPaymentTo}
                        style={{ height: TABLE_CONTROL_HEIGHT }}
                        placeholder="Mail payment to"
                        onChange={e => updateRow(row.id, current => ({ ...current, mailPaymentTo: e.target.value }))}
                      />
                    ) : (
                      <p className="text-muted text-center mb-0">-</p>
                    )}
                  </td>
                  <td className="align-middle text-center">
                    {rows.length > 1 || isRowPopulated(row) ? (
                      <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeRow(row.id)}>
                        &times;
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="mt-2">
        <button type="button" onClick={addRow} className="btn w-100 py-2 utility-add-row-btn">
          + Add row
        </button>
        <div
          className="d-flex justify-content-end align-items-center mt-2 px-3 py-2"
          style={{ backgroundColor: '#f4f5f7', borderRadius: 10 }}
        >
          <span className="mr-2 text-muted">Total</span>
          <h6 className="mb-0 font-weight-bold">
            $
            {totalAmount.toLocaleString('en-US', {
              minimumFractionDigits: MAX_DECIMAL_PLACES,
              maximumFractionDigits: MAX_DECIMAL_PLACES
            })}
          </h6>
        </div>
      </div>
      {showValidationErrors &&
      rows.some(row => hasProvider(row.accountType) && !row.provider && row.serviceTypes.length > 0) ? (
        <p className="text-danger small mb-0 mt-2">
          Provider is required for Individual and Well &amp; Septic account types.
        </p>
      ) : null}
    </div>
  );
};

export default UtilityServicesTableInput;
